/**
 * Option sets for the library's filter bar and its "All filters" slideout
 * (CT-5186).
 *
 * Every value here is a value `POST /v0/dashboard/widget/list` actually
 * accepts — `categoryKeys`, `typeCodes`, `status`, `isGoverned`. Profile is
 * the one catalog-driven dimension: its options come from
 * `section/list_filter` at runtime (`useProfileFilterOptions`) and go out as
 * `sectionKeys`, so it has no constant here. Freshness is gone: the
 * WidgetDefinition contract has no such field.
 *
 * Labels are i18n **keys**, never literals, so nothing user-facing is inlined
 * in JSX.
 */

/**
 * Widget categories — the backend's own `STATISTICS | DATA_VIEW | CONSUMPTION`
 * vocabulary, sent as `categoryKeys`.
 *
 * TODO(CT-5183 / backend): source these from `listWidgetCategories()` once
 * `/widget/category/list_filter` returns rows; its repository is a stub today
 * and an empty catalog would leave the filter with no options at all.
 */
export const CATEGORY_OPTIONS = [
  { value: 'STATISTICS', labelKey: 'widgetLibrary.filters.category.statistics' },
  { value: 'DATA_VIEW', labelKey: 'widgetLibrary.filters.category.dataView' },
  { value: 'CONSUMPTION', labelKey: 'widgetLibrary.filters.category.consumption' },
];

/** Lifecycle status, sent as `status`. */
export const STATUS_OPTIONS = [
  { value: 'DRAFT', labelKey: 'widgetLibrary.filters.status.draft' },
  { value: 'PUBLISHED', labelKey: 'widgetLibrary.filters.status.published' },
];

/**
 * Governance, sent as the boolean `isGoverned`. Kept as strings because the
 * slideout's single-select sections are string-valued and `''` is their
 * "no filter" sentinel — a tri-state that a boolean cannot carry.
 */
export const GOVERNED_OPTIONS = [
  { value: 'true', labelKey: 'widgetLibrary.filters.governed.governed' },
  { value: 'false', labelKey: 'widgetLibrary.filters.governed.notGoverned' },
];

/**
 * Presentation types, sent as `typeCodes`. These are the 18 types
 * `@aimsos/ui`'s WidgetGrid can actually render, so they stay local rather
 * than coming from `listWidgetTypes()` — a type the frontend has no component
 * for is not an option the filter can offer.
 */
export const TYPE_OPTIONS = [
  { value: 'kpi', labelKey: 'widgetLibrary.types.kpi' },
  { value: 'stat_row', labelKey: 'widgetLibrary.types.statRow' },
  { value: 'gauge', labelKey: 'widgetLibrary.types.gauge' },
  { value: 'chart', labelKey: 'widgetLibrary.types.chart' },
  { value: 'donut', labelKey: 'widgetLibrary.types.donut' },
  { value: 'funnel', labelKey: 'widgetLibrary.types.funnel' },
  { value: 'heat_map', labelKey: 'widgetLibrary.types.heatMap' },
  { value: 'map', labelKey: 'widgetLibrary.types.map' },
  { value: 'ai_summary', labelKey: 'widgetLibrary.types.aiSummary' },
  { value: 'table', labelKey: 'widgetLibrary.types.table' },
  { value: 'list', labelKey: 'widgetLibrary.types.list' },
  { value: 'board', labelKey: 'widgetLibrary.types.board' },
  { value: 'feed', labelKey: 'widgetLibrary.types.feed' },
  { value: 'alerts', labelKey: 'widgetLibrary.types.alerts' },
  { value: 'cost_kpi', labelKey: 'widgetLibrary.types.costKpi' },
  { value: 'usage_heatmap', labelKey: 'widgetLibrary.types.usageHeatmap' },
  { value: 'spend_breakdown', labelKey: 'widgetLibrary.types.spendBreakdown' },
  { value: 'composite_stat', labelKey: 'widgetLibrary.types.compositeStat' },
];

/**
 * Sort fields, sent as `orderBy`. Only `name`: the endpoint accepts any string
 * without rejecting it, so offering a field it does not sort on would be a
 * control that looks like it works and changes nothing. `most_used` went with
 * the placement count the contract does not expose.
 */
export const SORT_FIELD_OPTIONS = [
  { value: 'name', labelKey: 'widgetLibrary.filters.sort.name' },
];

/** The list reads best alphabetically, so `asc` is the default direction here. */
export const DEFAULT_SORT_FIELD = 'name';
export const DEFAULT_SORT_DIRECTION = 'asc';

/** How long the box holds a keystroke before it reaches the request. */
export const SEARCH_DEBOUNCE_MS = 400;
